import { Button, DataTable } from '@hai/ui-react';
import { HaiDataTableColumnType, HaiSortDirection } from '@hai/ui-react/dist/types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APIPath } from 'src/js/api/route-path-index';
import { compileWithArgs } from 'src/js/helper/path-to-regexp-helper';
import { useStores } from 'src/js/hook/use-stores';
import { constant } from 'src/js/constant';
import { useLocalStorage } from 'usehooks-ts';
import { ListSelector } from 'src/js/util/listSelector';
import { presetName, sortDataTable } from './preset-helper';
import { renderActiveIcon } from './preset-load-table';
import { Preset, PresetViewModel, Presets } from './preset-models';
import { savePresetWithoutChangingActive, updatePreset } from './preset-store';
import { useStateWatch, useWatchObject } from 'src/js/store/use-watch';
import { scrollToId } from 'src/js/helper/scroll-helper';

interface Props {
  listController: ListSelector<PresetViewModel>;
  presets: Presets;
  hilited: string;
  onDelete: VoidFunction;
  onDuplicate: (preset: Preset) => void;
  onLoadClicked: (name: string, notification: boolean) => Promise<any> | void;
  onRename: VoidFunction;
  onSetAsStartup: (preset: PresetViewModel) => void;
  shouldReloadData: () => void;
  setSingleItem: (preset: Preset) => void;
}

export const PresetManagerTable: React.FunctionComponent<Props> = observer(
  ({
    listController,
    presets,
    hilited,
    onDelete,
    onDuplicate,
    onLoadClicked,
    onRename,
    onSetAsStartup,
    shouldReloadData,
    setSingleItem,
  }) => {
    const { t } = useTranslation();
    const { sessionStore } = useStores();
    const [count, setcount] = useState(1);
    const [fieldKey, setFieldKey] = useLocalStorage(
      `${constant.lStorage.presetTable}:fieldKey:${sessionStore.username}`,
      'name',
    );
    const [sortFieldKey, setSortFieldKey] = useLocalStorage(
      `${constant.lStorage.presetTable}:sortFieldKey:${sessionStore.username}`,
      'name',
    );
    const [direction, setDirection] = useLocalStorage<HaiSortDirection>(
      `${constant.lStorage.presetTable}:directionKey:${sessionStore.username}`,
      HaiSortDirection.ASC,
    );

    const onChangeSort = (fieldKey: string, order: HaiSortDirection, sortFieldKey: string) => {
      setFieldKey(fieldKey);
      setDirection(order);
      setSortFieldKey(sortFieldKey);
    };

    const bulkActionComponents = () => {
      return (
        <Button state="idle" size="small" onClick={onDelete}>
          {t('general.deleteBtn')}
        </Button>
      );
    };

    // Add title to presets that are too long
    useEffect(() => {
      const timeout = setTimeout(() => {
        document.querySelectorAll('.preset-table .text-data').forEach((e: any) => {
          if (e.offsetWidth < e.scrollWidth) {
            e.setAttribute('title', e.textContent);
          }
        });
      }, 300);
      setInterval(() => {
        setcount(count + 1);
      }, 500);
      return () => {
        clearTimeout(timeout);
      };
    }, []);

    useEffect(() => {
      const element = document.getElementById(hilited);
      if (element) {
        scrollToId(hilited, 48);
      }
    }, [hilited]);

    const handleAction = (eventKey: string, data: any) => {
      switch (eventKey) {
        case 'save':
          if (listController.list[data.index].name === presets.active) {
            const displaySuccess = true;
            const overwrite = true;
            updatePreset(listController.list[data.index], overwrite, displaySuccess, t).then((_res) => {
              // amber bullet 'unsaved' to dissapear after save and update modified time
              shouldReloadData();
            });
          } else {
            savePresetWithoutChangingActive(listController.list[data.index], t).then((_res) => {
              shouldReloadData();
            });
          }
          break;

        case 'load':
          onLoadClicked(data.name, true);
          break;

        case 'rename':
          {
            listController.unselectAll();
            const item = listController.list.find((i) => i.name === data.id);
            setSingleItem(item);
            onRename();
          }
          break;

        case 'duplicate':
          {
            const item = listController.list.find((i) => i.name === data.id);
            onDuplicate(item);
          }
          break;

        case 'download':
          window.location.assign(compileWithArgs(APIPath.presets.download)({ name: data.name }));
          break;

        case 'delete':
          {
            const toDelete = listController.list.find((i) => i.name === data.id);
            setSingleItem(toDelete);
            onDelete();
          }
          break;

        default:
          break;
      }
    };

    const radioButton = (preset: PresetViewModel) =>
      (preset.startup ? 'RadioSelected' : 'RadioUnselected') + (presets.autosave ? 'Disabled' : '');

    const [selectAll, setSelectAll] = useState(false);
    const handleCheckAll = (checked: boolean | string) => {
      setSelectAll(!!checked);
      checked ? listController.selectAll() : listController.unselectAll();
    };

    const selectedMatrix = useStateWatch(listController, 'rowMatrix', listController.rowMatrix);

    useWatchObject(listController, (_e) => {
      setcount((v) => v + 1);
    });
    // console.log('selected', listController.selectedRows(), selectedMatrix);
    return (
      <div className={classNames('preset-table scrollable', presets.autosave && 'autoSave')}>
        <DataTable
          selectable={true}
          sortFunction={sortDataTable}
          sortable={true}
          allowSortNone={false}
          initialSort={{ fieldKey: fieldKey, order: direction, sortFieldKey: sortFieldKey }}
          onSort={onChangeSort}
          columnStructure={[
            { fieldKey: 'name', useFsMask: true, title: t('general.name') },
            {
              fieldKey: 'activeJSX',
              useFsMask: true,
              title: t('presets.active'),
              alignment: 'center',
              sortFieldKey: 'active',
              className: 'presetManagerTableActiveColumn',
            },
            { fieldKey: 'modifiedTime', title: t('presets.modifiedTime'), sortFieldKey: 'mtime' },
            {
              fieldKey: 'startup',
              title: t('presets.startup'),
              type: HaiDataTableColumnType.ICON,
              className: 'presetTableStartupColumn',
            },
            { fieldKey: '', title: '', type: HaiDataTableColumnType.ACTIONS },
          ]}
          onCheckAll={handleCheckAll}
          checkAll={selectAll}
          className="datatable-modal"
          // maxHeight="378px" done with CSS instead see presets.scss .datatable-overflow-container
          emptyStateText={t('presets.addFirst')}
        >
          <DataTable.Header bulkActions={bulkActionComponents} />
          {listController.list.map((value: PresetViewModel, index: number) => (
            <DataTable.Row
              key={`${value.name}`}
              //id={`${value.name}`}
              {...{ id: `${value.name}` }}
              rowData={{
                index: index,
                id: `${value.name}`,
                name: presetName(value.name),
                activeJSX: renderActiveIcon(
                  t,
                  value.name,
                  presets.active,
                  presets.autosave,
                  presets.activeWasModified,
                  listController.toggleRow,
                ),
                active: value.name === presets.active,
                modifiedTime: moment.unix(Number(value.mtime)).format(constant.momentFormat.presetDateTime),
                mtime: value.mtime,
                startup: radioButton(value),
              }}
              className={hilited === value.name && 'hilited'}
              onSelect={() => listController.toggleRow(value.name)}
              //    onClick={() => listController.toggleRow(value.name)}
              checked={selectedMatrix[index]} // TODO add a unit test to cover a new item inserted and shifting of indexes
              onIconClick={() => onSetAsStartup?.(value)}
              actionItems={[
                {
                  actionIcon: 'Save',
                  title: t('general.save'),
                  eventKey: 'save',
                  onSelect: handleAction,
                },
                {
                  actionIcon: 'Import',
                  title: t('general.load'),
                  eventKey: 'load',
                  onSelect: handleAction,
                },
                {
                  actionIcon: 'Edit',
                  title: t('general.rename'),
                  eventKey: 'rename',
                  onSelect: handleAction,
                },
                {
                  actionIcon: 'Duplicate',
                  title: t('general.duplicate'),
                  eventKey: 'duplicate',
                  onSelect: handleAction,
                },
                {
                  actionIcon: 'Download',
                  title: t('general.download'),
                  eventKey: 'download',
                  onSelect: handleAction,
                },
                {
                  actionIcon: 'TrashCan',
                  title: t('general.delete'),
                  eventKey: 'delete',
                  onSelect: handleAction,
                },
              ]}
            ></DataTable.Row>
          ))}
        </DataTable>
      </div>
    );
  },
);
